import { Field, Form, Formik } from "formik"
import useTranslation from "next-translate/useTranslation"
import { FormGroup, Label } from "reactstrap"

import { FeedbackRoles, ImplementationState, IProject, ProjectState } from "@api/schema"
import { FormButtonArea } from "@components/common/form/FormButtonArea"
import FormikCheckbox from "@components/common/form/FormikCheckbox"
import FormikSelect from "@components/common/form/FormikSelect"
import GeneralFormError from "@components/common/form/GeneralFormError"
import { IFormikActions } from "@redux/helper/actions"
import { MODULE_FEEDBACK_FOR_PROJECTS_AVAILABLE } from "config"


interface IProps {
  onSubmit: (...args: any) => void
  project: IProject
  toggle: () => void
}


/**
 * this component provides a form to change the state of a single project: planning, running, finished, active/inactive
 *
 * @todo: move the file to a better folder?
 */
const ProjectStateForm: React.FC<IProps> = ({ onSubmit, project, toggle }: IProps) => {
  const { t } = useTranslation("common")

  // separate submit-function to compile only those data-elements, altered in this form
  const onFormSubmit = (values: { deactivated: boolean; implementationState: ImplementationState; feedbackAllowedFor: FeedbackRoles[] }, actions: IFormikActions): void => {

    // only update those information provided by this form, let other project data untouched
    const updatedProject: IProject = {
      "@id": project["@id"],
      feedbackAllowedFor: values.feedbackAllowedFor,
      implementationState: values.implementationState,
      state: values.deactivated ? ProjectState.Deactivated : ProjectState.Active
    }
    onSubmit(updatedProject, actions)
  }

  return <Formik<{ deactivated: boolean; implementationState: ImplementationState; feedbackAllowedFor: FeedbackRoles[] }>
    initialValues={{
      deactivated: project.state === ProjectState.Deactivated,
      implementationState: project.implementationState,
      feedbackAllowedFor: project.feedbackAllowedFor,
    }}
    onSubmit={onFormSubmit}
    validateOnBlur={false}
    validateOnChange={false}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting,
      values
    }) => <Form onSubmit={handleSubmit}>
        <GeneralFormError
          errors={errors}
          prefix="project"
          excludeFields={['implementationState', 'deactivated']}
        />

        <Field component={FormikSelect}
          help="form.project.implementationState.help"
          label="form.project.implementationState.label"
          name="implementationState"
          options={{
            [t("project.implementationState." + ImplementationState.Planning)]: ImplementationState.Planning,
            [t("project.implementationState." + ImplementationState.Running)]: ImplementationState.Running,
            [t("project.implementationState." + ImplementationState.Finished)]: ImplementationState.Finished,
          }}
          required
          value={values.implementationState}
        />

        <Field component={FormikCheckbox}
          help="form.project.deactivated.help"
          label="form.project.deactivated.label"
          name="deactivated"
          checked={values.deactivated}
        />

        {MODULE_FEEDBACK_FOR_PROJECTS_AVAILABLE && <>
          <div id="checkbox-group">{t("form.project.feedbackAllowedFor.label", { noOne: !values.feedbackAllowedFor || values.feedbackAllowedFor.length === 0 ? t("feedback.discussion.feedbackAllowedFor.noOne") : "" })}</div>
          <small className="form-text text-muted">{t("form.project.feedbackAllowedFor.help")}</small>
          <FormGroup name="feedbackAllowedFor" role="group" aria-labelledby="checkbox-group">
            <div className="form-group-checkbox">
              <Label check>
                <Field type="checkbox" name="feedbackAllowedFor" className="form-check-input" value={FeedbackRoles.Coordinator} disabled={values.feedbackAllowedFor?.includes(FeedbackRoles.RegisteredUser)} />
                {t("feedback.discussion.feedbackAllowedFor." + FeedbackRoles.Coordinator)}
              </Label>
            </div>
            <div className="form-group-checkbox">
              <Label check>
                <Field type="checkbox" name="feedbackAllowedFor" className="form-check-input" value={FeedbackRoles.Planner} disabled={values.feedbackAllowedFor?.includes(FeedbackRoles.RegisteredUser)} />
                {t("feedback.discussion.feedbackAllowedFor." + FeedbackRoles.Planner)}
              </Label>
            </div>
            <div className="form-group-checkbox">
              <Label check>
                <Field type="checkbox" name="feedbackAllowedFor" className="form-check-input" value={FeedbackRoles.Observer} disabled={values.feedbackAllowedFor?.includes(FeedbackRoles.RegisteredUser)} />
                {t("feedback.discussion.feedbackAllowedFor." + FeedbackRoles.Observer)}
              </Label>
            </div>
            <div className="form-group-checkbox">
              <Label check>
                <Field type="checkbox" name="feedbackAllowedFor" className="form-check-input" value={FeedbackRoles.ProcessManager} disabled={values.feedbackAllowedFor?.includes(FeedbackRoles.RegisteredUser)} />
                {t("feedback.discussion.feedbackAllowedFor." + FeedbackRoles.ProcessManager)}
              </Label>
            </div>
            <div className="form-group-checkbox">
              <Label check>
                <Field type="checkbox" name="feedbackAllowedFor" className="form-check-input" value={FeedbackRoles.RegisteredUser} />
                {t("feedback.discussion.feedbackAllowedFor." + FeedbackRoles.RegisteredUser)}
              </Label>
            </div>
          </FormGroup>
        </>}

        <p className="text-danger">{t("form.requiredFieldsHint")}</p>

        <FormButtonArea
          isSubmitting={isSubmitting}
          submitButton={{
            title: "form.submit",
          }}
          cancelButton={{ onClick: toggle }}
        />
      </Form>
    }
  </Formik>
}

export default ProjectStateForm
